/**
 * NavBar -- Navigation bar con 2 secciones audience-aware (Operativo + Nexo Internal).
 *
 * PANEL-ARTIFACTS-AUDIENCE-V1-001 / T2:
 *   - Cada NAV_LINK ahora tiene `audience` ('business' | 'nexo' | 'both').
 *   - getVisibleLinks() filtra por audience efectiva del usuario via useAudience.
 *   - Renderiza 2 grupos: Operativo (audience=business) + Nexo Internal (audience=nexo).
 *   - Si user es business-locked: NO se muestra el grupo Internal.
 *   - audience='both' va pegado al Dashboard, antes de los grupos (menuDelPanel.js).
 *     Hasta el 11-oct solo se pintaba el Dashboard: Aprobaciones nunca salio (Q #25679).
 *
 * @module presentation/components/NavBar
 */

import { AuthService } from '../../application/AuthService.js';
import { AppConfig } from '../../config.js';
import { ARTIFACT_ROLES } from '../../domain/entities/Item.js';
import { resolveUserAudience, audienceMatches } from '../shared/useAudience.js';
import { getSelectedBusiness, getSelectedBusinessName } from './BusinessSelector.js';
import { gruposDelMenu } from './menuDelPanel.js';

// Cuenta de negocio: login devolvio businessId (nx_business_locked) y no es dev/admin.
// Cubre role=client y role=manager con business lockeado.
function isBusinessUser() {
  const locked = localStorage.getItem('nx_business_locked') === 'true';
  const role = AppConfig.role;
  return locked && role !== 'dev' && role !== 'admin';
}

/**
 * Setea el titulo de pagina en la navbar (visible solo para role-client).
 * Cada vista llama esto en su render para que el titulo aparezca en el navbar
 * (patron similar a MarcacionView/Kiosk). El Dashboard llama con "" para limpiar.
 */
export function setPageTitle(text) {
  const el = document.getElementById('nx-page-title');
  if (el) el.textContent = text || '';
}

// ============================================================================
// NAV_LINKS con audience tag (T2 fix)
// audience: 'business' | 'nexo' | 'both'
// ============================================================================
const NAV_LINKS = [
  // === Always visible (both) ===
  { hash: '#/',                  label: 'Dashboard',     audience: 'both' },
  // APR-BOTONES-T1 (Josse 30-sep): lo que la caja le pide aprobar al dueno, a un clic.
  { hash: '#/notificaciones',    label: 'Aprobaciones',  audience: 'both' },

  // === BUSINESS (Operativo dia-a-dia negocio) ===
  { hash: '#/negocios',          label: 'Negocios',      audience: 'business' },
  { hash: '#/inventario',        label: 'Inventario',    audience: 'business' },
  { hash: '#/carrito',           label: 'Carrito',       audience: 'business' },
  { hash: '#/colaboradores',     label: 'Colaboradores', audience: 'business' },
  { hash: '#/horarios',          label: 'Horarios',      audience: 'business' },
  { hash: '#/asistencia',        label: 'Asistencia',    audience: 'business' },
  { hash: '#/marcacion',         label: 'Kiosk',         audience: 'business' },
  { hash: '#/compras',           label: 'Compras',       audience: 'business' },
  { hash: '#/presupuestos',     label: 'Presupuestos',  audience: 'business' },
  { hash: '#/saldos',            label: 'Saldos',        audience: 'business' },
  { hash: '#/informes',          label: 'Informes',      audience: 'business' },
  { hash: '#/ingresos',          label: 'Ingresos',      audience: 'business' },
  { hash: '#/egresos',           label: 'Egresos',       audience: 'business' },
  { hash: '#/creditos',          label: 'Creditos',      audience: 'business' },
  { hash: '#/banco',             label: 'Banco',         audience: 'business' },
  { hash: '#/auditoria',         label: 'Auditoria',     audience: 'business' },
  { hash: '#/publicidad',        label: 'Publicidad',    audience: 'business' },
  { hash: '#/capacitacion',      label: 'Capacitacion',  audience: 'business' },
  { hash: '#/notes',             label: 'Notas',         audience: 'business' },

  // === NEXO INTERNAL (equipo Nexo) ===
  { hash: '#/missions',          label: 'Misiones',      audience: 'nexo' },
  { hash: '#/tasks',             label: 'Tareas',        audience: 'nexo' },
  { hash: '#/agents',            label: 'Agentes',       audience: 'nexo' },
  { hash: '#/agents-dashboard',  label: 'Equipo Argys',  audience: 'nexo' },
  { hash: '#/instructions',      label: 'Instrucciones', audience: 'nexo' },
  { hash: '#/knowledge',         label: 'Conocimiento',  audience: 'nexo' },
  { hash: '#/skills',            label: 'Skills',        audience: 'nexo' },
  { hash: '#/pensum',            label: 'Pensum',        audience: 'nexo' },
  { hash: '#/funcionalidades',   label: 'Funcionalidades', audience: 'nexo' },
  { hash: '#/arbol',             label: 'Arbol',         audience: 'nexo' },
  { hash: '#/items',             label: 'Items',         audience: 'nexo' },
  { hash: '#/projects',          label: 'Proyectos',     audience: 'nexo' },
  { hash: '#/agent-quality',     label: 'Agent Quality', audience: 'nexo' },
  { hash: '#/nexo-pagos',        label: 'Pagos Nexo',    audience: 'nexo' },
  { hash: '#/contexto',          label: 'Accesos de asistentes', audience: 'nexo' },
  { hash: '#/decisiones',        label: 'Lo que necesita de vos', audience: 'nexo' },
  { hash: '#/users',             label: 'Usuarios',      audience: 'nexo' },
  { hash: '#/personas',          label: 'Personas',      audience: 'nexo' }
];

/**
 * Filtra NAV_LINKS por:
 *   1. ARTIFACT_ROLES (permisos por role del Item.js)
 *   2. audience efectiva del usuario (T2 fix)
 *
 * Reglas anidadas:
 *   - dev/admin: ven todos los links permitidos por rol (audience='all')
 *   - business-locked: solo audience='business' o 'both'
 *   - nexo-only (member): solo audience='nexo' o 'both'
 */
export function getVisibleLinks() {
  const role = AppConfig.role;
  const allowed = ARTIFACT_ROLES[role];
  const userAudience = resolveUserAudience();

  return NAV_LINKS.filter(l => {
    // Marcacion (Kiosk) y Dashboard siempre visibles, independiente de role
    if (l.hash === '#/' || l.hash === '#/marcacion') return true;

    // Filtro 1: role permission
    if (allowed && !allowed.includes(l.hash)) return false;

    // Filtro 2: audience match
    return audienceMatches(l.audience, userAudience);
  });
}

/**
 * Construye el HTML de los links agrupando en 2 secciones visuales:
 *   - Operativo (audience=business + both no-Dashboard)
 *   - Nexo Internal (audience=nexo)  [hidden si user audience='business']
 *
 * Si user audience='all' (dev/admin/manager-nexo): muestra ambas secciones.
 * Si user audience='business': solo seccion Operativo.
 * Si user audience='nexo': solo seccion Nexo Internal (no le sirve operativo).
 */
export function buildLinksHtml(links) {
  const linkHtml = (l) =>
    `<a href="${l.hash}" class="nav-link" data-hash="${l.hash}" data-audience="${l.audience}">${l.label}</a>`;
  const sepHtml = (label) =>
    `<span class="nav-separator" data-section-label="${label}" title="Seccion ${label}"></span>`;

  // El orden y los grupos los decide menuDelPanel (lo mide check:bandeja-aprobaciones).
  return gruposDelMenu(links, resolveUserAudience())
    .map(p => (p.tipo === 'separador' ? sepHtml(p.etiqueta) : linkHtml(p.enlace)))
    .join('');
}

export function setupNavBar(onLogout) {
  const $navLinks = document.getElementById('nx-nav-links');

  const visibleLinks = getVisibleLinks();
  $navLinks.innerHTML = buildLinksHtml(visibleLinks);

  // Titulo de pagina en navbar (visible solo en role-client via CSS). Vacio por default;
  // cada vista lo setea con setPageTitle(text) al renderizar (patron tipo breadcrumb).
  const $nav = document.getElementById('nx-nav');
  if ($nav && !document.getElementById('nx-page-title')) {
    const $title = document.createElement('h2');
    $title.id = 'nx-page-title';
    $title.className = 'nav-page-title';
    $nav.insertBefore($title, $navLinks);
  }

  // Botón de configuración/sistema + selector de rol
  const $actions = document.querySelector('.nav-actions');
  if ($actions && !document.getElementById('btn-system')) {
    // Role selector (dev only visible)
    if (AppConfig.role === 'dev' || AppConfig.role === 'admin') {
      const $role = document.createElement('select');
      $role.id = 'nx-role-select';
      $role.className = 'form-input form-input-sm';
      $role.style.cssText = 'font-size:0.875rem;width:auto;padding:2px 4px;opacity:0.7';
      $role.title = 'Modo de vista';
      $role.innerHTML = ['dev', 'member', 'manager', 'client'].map(r =>
        `<option value="${r}"${r === AppConfig.role ? ' selected' : ''}>${r}</option>`
      ).join('');
      $role.onchange = () => {
        AppConfig.setRole($role.value);
        window.location.reload();
      };
      $actions.insertBefore($role, $actions.firstChild);
    }

    // Boton ⚙ estado del sistema - solo para usuarios internos Nexo. En negocio NO aparece.
    if (!isBusinessUser()) {
      const $btn = document.createElement('button');
      $btn.id = 'btn-system';
      $btn.className = 'btn-settings';
      $btn.type = 'button';
      $btn.title = 'Estado del sistema';
      $btn.innerHTML = '&#9881;';
      $btn.onclick = () => { window.location.hash = '#/system'; };
      $actions.insertBefore($btn, $actions.firstChild);
    }

    // Boton "Inicio" (casita) - solo para role=client, permite volver al dashboard desde
    // cualquier vista (kiosk, inventario, carrito). Global, siempre visible en navbar.
    if (isBusinessUser()) {
      const $home = document.createElement('button');
      $home.id = 'btn-home';
      $home.className = 'btn-ghost';
      $home.type = 'button';
      $home.title = 'Volver al dashboard';
      $home.style.cssText = 'font-size:1.1rem;padding:4px 10px';
      $home.innerHTML = '&#127968;'; // 🏠
      $home.onclick = () => { window.location.hash = '#/'; };
      $actions.insertBefore($home, $actions.firstChild);
    }
  }

  document.getElementById('btn-logout').onclick = () => {
    AuthService.logout();
    onLogout();
  };

  updateActiveLink();
  window.addEventListener('hashchange', updateActiveLink);

  aplicarModoEnfoque();
  window.addEventListener('hashchange', aplicarModoEnfoque);
  // Reversible: al cambiar de negocio el panel vuelve a ser el panel completo.
  window.addEventListener('business:changed', alCambiarNegocio);
}

/**
 * MODO ENFOQUE (feedback Josse #8476, arreglado en DEMO-BUG-ENFOQUE-T1).
 *
 * AUDIENCIA: CLIENTE FINAL en la demo proyectada. Cuando se le muestra la
 * herramienta a un cliente, el navbar del panel (Carrito, Inventario,
 * Colaboradores...) no lo ayuda: lo confunde y le ensena la cocina de otro
 * negocio. En enfoque se apaga todo eso y queda una mini-cabecera con lo suyo.
 *
 * EL BUG QUE ESTO ARREGLA (reporte de Josse, 26-ago): la version anterior decia
 * "se apaga solo al cambiar de negocio: no hay estado que limpiar a mano".
 * Era mentira, y la mentira estaba a 200 lineas de distancia en layout.css:
 * `.nav-bar.nx-enfoque #nx-business-select-wrap { display: none !important }`.
 * El modo ESCONDIA EL SELECTOR DE NEGOCIO, que era su unica salida. Quien
 * entraba a la demo quedaba encerrado: sin navbar, sin selector, sin volver.
 * Y el panel es la herramienta de trabajo diaria, no solo el escenario.
 *
 * REGLA QUE SE SIGUE AHORA: **un modo que oculta la salida tiene que traer la
 * suya propia.** La puerta viaja con la mini-cabecera, que es lo unico que
 * queda visible; y ademas hay un apagado explicito que gana sobre todo lo demas
 * (?enfoque=0 y una llave persistida) para que salir funcione siempre, incluso
 * si no sabemos de que negocio venia.
 */
const NEGOCIO_DEMO = 'biz_demo_maderinco';
const ENFOQUE_APAGADO_KEY = 'nx_enfoque_off';   // salida explicita del usuario
const NEGOCIO_PREVIO_KEY = 'nx_negocio_previo'; // a donde volver al salir
const NEGOCIO_PREVIO_NOMBRE_KEY = 'nx_negocio_previo_nombre';
const DEMO_PASO_KEY = 'nx_demo_step';           // el paso del teatro (PresupuestosView)

function urlTiene(valor) {
  const re = new RegExp('[?&]enfoque=' + valor);
  return re.test(window.location.search) || re.test(window.location.hash);
}

export function enfoqueActivo() {
  // El apagado explicito manda sobre todo: es la salida de emergencia y no
  // depende de ningun control que el propio modo pueda estar escondiendo.
  if (urlTiene('0')) return false;
  if (urlTiene('1')) return true;
  if (getSelectedBusiness() !== NEGOCIO_DEMO) return false;
  return localStorage.getItem(ENFOQUE_APAGADO_KEY) !== '1';
}

/**
 * A donde salir cuando no se sabe de donde vino. Salir tiene que SALIR: si el
 * negocio se queda en el demo, el selector se vuelve inutil (elegir la opcion
 * que ya esta puesta no dispara change) y no hay forma de volver a entrar.
 * Eso fue exactamente el bug que reporto Josse.
 */
function otroNegocioDelSelector() {
  const $sel = document.querySelector('#nx-business-select-wrap select');
  if (!$sel) return null;
  const $opt = Array.from($sel.options).find(o => o.value && o.value !== NEGOCIO_DEMO);
  return $opt ? { id: $opt.value, nombre: $opt.dataset.name || $opt.value } : null;
}

/** El nombre de un negocio, leido del selector real. Si no esta, se usa el id. */
function nombreDeNegocio(id) {
  const $sel = document.querySelector('#nx-business-select-wrap select');
  if (!$sel) return id;
  const $opt = Array.from($sel.options).find(o => o.value === id);
  return ($opt && $opt.dataset.name) || id;
}

/**
 * Salir del modo demo. Es la puerta, y tiene que funcionar SIEMPRE:
 * con negocio anterior conocido o sin el.
 */
function salirDelModoDemo() {
  // 1. El teatro vuelve al principio: si no, al reentrar la demo arranca a medias.
  try { localStorage.removeItem(DEMO_PASO_KEY); } catch (e) { /* modo privado */ }

  // 2. El apagado explicito. Se pone SIEMPRE, no solo cuando no hay negocio
  //    previo: es lo que garantiza que la salida no dependa de nada mas.
  try { localStorage.setItem(ENFOQUE_APAGADO_KEY, '1'); } catch (e) { /* modo privado */ }

  // 3. Si la URL traia ?enfoque=1 hay que neutralizarlo, o al repintar vuelve
  //    solo. Se REESCRIBE a 0 en vez de borrarlo: borrar el parametro puede
  //    dejar un query string roto (?enfoque=1&a=2 -> &a=2), y ademas el 0 es
  //    la senal explicita que enfoqueActivo() mira primero.
  if (urlTiene('1')) {
    const aCero = (t) => String(t || '').split('enfoque=1').join('enfoque=0');
    window.history.replaceState(null, '',
      window.location.pathname + aCero(window.location.search) + aCero(window.location.hash));
  }

  // 4. Volver al negocio anterior, si lo sabemos. Se reemite business:changed
  //    para que TODAS las vistas reaccionen igual que si el usuario hubiera
  //    usado el selector -- no basta con escribir en localStorage.
  let previo = null, nombreGuardado = null;
  try {
    previo = localStorage.getItem(NEGOCIO_PREVIO_KEY);
    nombreGuardado = localStorage.getItem(NEGOCIO_PREVIO_NOMBRE_KEY);
  } catch (e) { previo = null; }

  // Sin previo conocido (se entro por ?enfoque=1, o ya se salio una vez antes)
  // se sale al primer negocio real del selector. Quedarse en el demo dejaba al
  // usuario encerrado FUERA: negocio demo, modo apagado y sin manera de volver.
  if (!previo || previo === NEGOCIO_DEMO) {
    const otro = otroNegocioDelSelector();
    if (otro) { previo = otro.id; nombreGuardado = otro.nombre; }
  }

  if (previo && previo !== NEGOCIO_DEMO) {
    // El nombre se guarda AL ENTRAR, no se resuelve aqui: al salir el selector
    // esta oculto por el propio modo, asi que leerlo en este momento devuelve el
    // id pelado y el usuario ve "biz_exito_chino_001" donde deberia ver su
    // negocio. Medido en el smoke antes de arreglarlo.
    const nombre = nombreGuardado || nombreDeNegocio(previo);
    const anterior = getSelectedBusiness();
    try {
      localStorage.setItem('nx_selected_business', previo);
      localStorage.setItem('nx_selected_business_name', nombre);
      localStorage.removeItem(NEGOCIO_PREVIO_KEY);
      localStorage.removeItem(NEGOCIO_PREVIO_NOMBRE_KEY);
    } catch (e) { /* modo privado */ }
    const $sel = document.querySelector('#nx-business-select-wrap select');
    if ($sel) $sel.value = previo;
    window.dispatchEvent(new CustomEvent('business:changed', {
      detail: { businessId: previo, businessName: nombre, previousBusinessId: anterior }
    }));
  }

  // 5. Repintar el navbar y salir de la vista de la demo.
  aplicarModoEnfoque();
  if (String(window.location.hash || '').indexOf('#/presupuestos') === 0) window.location.hash = '#/';
}

/**
 * Segunda puerta de entrada: ir a #/presupuestos con el negocio demo puesto
 * ES querer entrar a la demo. Sin esto, el unico camino era cambiar de negocio
 * -- y si ya estabas en el demo no habia cambio posible que disparar.
 */
function reentrarSiCorresponde() {
  if (getSelectedBusiness() !== NEGOCIO_DEMO) return;
  if (urlTiene('0')) return; // apagado explicito en la URL: se respeta
  if (!String(window.location.hash || '').startsWith('#/presupuestos')) return;
  try { localStorage.removeItem(ENFOQUE_APAGADO_KEY); } catch (e) { /* modo privado */ }
}

function aplicarModoEnfoque() {
  reentrarSiCorresponde();
  const $nav = document.getElementById('nx-nav');
  if (!$nav) return;
  const activo = enfoqueActivo();
  $nav.classList.toggle('nx-enfoque', activo);
  // El body tambien, porque hay reglas que viven FUERA del navbar y necesitan
  // saber que estamos en demo. Concretamente: role-client esconde el regreso y
  // el titulo de toda vista (pensado para la tablet, donde el titulo vive en el
  // navbar y no hay navegacion). En demo el navbar TAMPOCO esta, asi que las
  // dos formas de ocultar se apilan y la pantalla queda sin una sola salida.
  // Con esta marca el CSS puede devolver la cabecera cuando de verdad hace falta.
  document.body.classList.toggle('nx-enfoque-on', activo);

  let $mini = document.getElementById('nx-mini-cabecera');
  if (!activo) { if ($mini) $mini.remove(); return; }

  const nombre = getSelectedBusinessName() || 'Tu negocio';
  const inicial = (nombre.trim()[0] || 'N').toUpperCase();
  if (!$mini) {
    $mini = document.createElement('div');
    $mini.id = 'nx-mini-cabecera';
    $mini.className = 'nx-mini-cabecera';
    const $links = document.getElementById('nx-nav-links');
    if ($links) $links.insertAdjacentElement('afterend', $mini);
  }
  $mini.innerHTML =
    '<span class="nx-mini-placa"></span>' +
    '<span class="nx-mini-nombre"></span>' +
    '<span class="nx-mini-sep">&middot;</span>' +
    '<button type="button" class="nx-mini-vista" data-demo-inicio title="Volver al asistente">Presupuestos</button>' +
    '<button type="button" class="nx-mini-reiniciar" data-demo-reiniciar title="Volver la demo al principio">Reiniciar demo</button>' +
    '<button type="button" class="nx-mini-salir" data-demo-salir title="Volver al panel completo">Salir del modo demo</button>';
  // Placa y nombre son DATO, no plantilla: van por textContent.
  $mini.querySelector('.nx-mini-placa').textContent = inicial;
  $mini.querySelector('.nx-mini-nombre').textContent = nombre;

  // El navbar no sabe de presupuestos: solo avisa. Quien pinta, repinta.
  // Breadcrumb vivo: el nombre de la vista lleva a la recepcion del asistente.
  // En demo es una salida MAS, siempre a la vista, sin depender de la cabecera
  // de la vista que se este mirando.
  $mini.querySelector('[data-demo-inicio]').addEventListener('click', () => {
    window.location.hash = '#/presupuestos';
    window.dispatchEvent(new CustomEvent('demo:inicio'));
  });
  $mini.querySelector('[data-demo-reiniciar]').addEventListener('click', () => {
    localStorage.setItem(DEMO_PASO_KEY, '0');
    window.dispatchEvent(new CustomEvent('demo:reset'));
  });
  $mini.querySelector('[data-demo-salir]').addEventListener('click', salirDelModoDemo);
}

/**
 * Al ENTRAR a la demo se recuerda de donde venia el usuario, para poder
 * devolverlo. El dato lo trae el propio evento del selector: no hay que
 * inventarse nada ni leerlo antes de tiempo.
 */
function alCambiarNegocio(e) {
  const nuevo = e && e.detail && e.detail.businessId;
  const previo = e && e.detail && e.detail.previousBusinessId;
  if (nuevo === NEGOCIO_DEMO) {
    if (previo && previo !== NEGOCIO_DEMO) {
      try {
        localStorage.setItem(NEGOCIO_PREVIO_KEY, previo);
        // Aqui el selector SI esta a la vista (el usuario acaba de usarlo), asi
        // que el nombre de verdad se puede leer. Al salir ya no.
        localStorage.setItem(NEGOCIO_PREVIO_NOMBRE_KEY, nombreDeNegocio(previo));
      } catch (err) { /* modo privado */ }
    }
    // Elegir Maderinco a mano es entrar a proposito: se limpia el apagado.
    // Sin esto, quien sale una vez no podria volver a entrar nunca -- y eso
    // seria romper la demo por arreglar el bug.
    try { localStorage.removeItem(ENFOQUE_APAGADO_KEY); } catch (err) { /* modo privado */ }
  }
  aplicarModoEnfoque();
}

/**
 * Ruta base para comparar: sin query y sin sub-ruta. Con match exacto,
 * #/presupuestos/123 no encendia NINGUN link y el usuario perdia de vista donde
 * estaba parado.
 */
function rutaBase(hash) {
  const limpio = String(hash || '#/').split('?')[0].replace(/\/+$/, '');
  const partes = limpio.split('/');
  return partes.length > 2 ? partes.slice(0, 2).join('/') : (limpio || '#/');
}

function updateActiveLink() {
  const current = rutaBase(window.location.hash || '#/');
  let $activo = null;
  document.querySelectorAll('#nx-nav-links .nav-link').forEach(a => {
    const encendido = rutaBase(a.dataset.hash) === current;
    a.classList.toggle('active', encendido);
    if (encendido) $activo = a;
  });
  // El navbar es un carril con scroll (20 items): si el activo queda fuera, se
  // ven items cortados y no se sabe en que vista se esta. Feedback Josse #8468.
  if ($activo && typeof $activo.scrollIntoView === 'function') {
    try { $activo.scrollIntoView({ inline: 'nearest', block: 'nearest' }); } catch (e) { /* navegador viejo */ }
  }
}
